import React from 'react';
import { formatDate } from '../../utils/dateFormatter';

export const useOrderPrint = () => {
  const handlePrint = (order) => {
    const printWindow = window.open('', '_blank');

    const statusLabel = order.status === 'completed' ? 'Completed' : order.status === 'cancelled' ? 'Cancelled' : 'Pending';
    const statusClass = order.status === 'completed' ? 'badge-success' : order.status === 'cancelled' ? 'badge-danger' : 'badge-warning';

    const variantsHtml = order.variants ? order.variants.map((v, idx) => `
      <tr>
        <td style="border: 1px solid #333; padding: 8px;">${idx + 1}</td>
        <td style="border: 1px solid #333; padding: 8px;">${v.size}</td>
        <td style="border: 1px solid #333; padding: 8px;">${v.color}</td>
        <td style="border: 1px solid #333; padding: 8px; text-align: center;">${v.quantity}</td>
      </tr>
    `).join('') : '';

    const totalQuantity = order.variants ? order.variants.reduce((sum, v) => sum + v.quantity, 0) : 0;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order #${order.order_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 30px; color: #000; background: #fff; }
            .container { max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333; }
            .header h1 { font-size: 22pt; margin-bottom: 5px; }
            .header p { font-size: 10pt; color: #666; }
            .doc-title { text-align: center; margin-bottom: 25px; }
            .doc-title h2 { font-size: 16pt; text-transform: uppercase; }
            .doc-title p { font-size: 14pt; margin-top: 5px; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 10pt; margin-left: 10px; }
            .badge-success { background: #d1fae5; color: #065f46; }
            .badge-warning { background: #fef3c7; color: #92400e; }
            .badge-danger { background: #fee2e2; color: #991b1b; }
            .info-grid { display: flex; justify-content: space-between; margin-bottom: 25px; }
            .info-left, .info-right { width: 48%; }
            .info-right { text-align: right; }
            .info-item { margin-bottom: 8px; }
            .info-label { font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
            th { background-color: #f0f0f0; border: 1px solid #333; padding: 10px; text-align: left; font-weight: bold; }
            td { border: 1px solid #333; padding: 8px; }
            .total-row { font-weight: bold; background: #f9f9f9; }
            .dates-section { margin: 20px 0; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }
            .dates-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
            .date-item { text-align: center; }
            .date-label { font-size: 10pt; color: #666; }
            .date-value { font-size: 12pt; font-weight: bold; margin-top: 3px; }
            .notes-section { margin-top: 20px; padding: 15px; background: #f9f9f9; border: 1px solid #ddd; }
            .notes-section h4 { margin-bottom: 8px; }
            .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
            .sig-line { width: 200px; border-top: 1px solid #333; padding-top: 5px; text-align: center; font-size: 10pt; }
            .footer { margin-top: 50px; padding-top: 15px; border-top: 1px solid #ccc; text-align: center; font-size: 9pt; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>CLADAKIS FOR READY MADE GARMENTS</h1>
              <p>Garment Manufacturing & Trading</p>
            </div>
            <div class="doc-title">
              <h2>Production Order</h2>
              <p>#${order.order_number} <span class="status-badge ${statusClass}">${statusLabel}</span></p>
            </div>
            <div class="info-grid">
              <div class="info-left">
                <div class="info-item"><span class="info-label">Client:</span> ${order.client_name}</div>
                <div class="info-item"><span class="info-label">Item Code:</span> ${order.item_code}</div>
                <div class="info-item"><span class="info-label">Description:</span> ${order.items}</div>
              </div>
              <div class="info-right">
                <div class="info-item"><span class="info-label">Order No:</span> ${order.order_number}</div>
                ${order.card_number ? `<div class="info-item"><span class="info-label">Card No:</span> ${order.card_number}</div>` : ''}
                <div class="info-item"><span class="info-label">Created:</span> ${formatDate(order.created_at)}</div>
              </div>
            </div>

            <h4 style="margin-bottom: 10px;">Size & Color Variants</h4>
            <table>
              <thead>
                <tr>
                  <th style="width: 50px;">#</th>
                  <th>Size</th>
                  <th>Color</th>
                  <th style="width: 100px; text-align: center;">Quantity</th>
                </tr>
              </thead>
              <tbody>
                ${variantsHtml}
                <tr class="total-row">
                  <td colspan="3" style="border: 1px solid #333; padding: 8px; text-align: right;">Total Quantity:</td>
                  <td style="border: 1px solid #333; padding: 8px; text-align: center;">${totalQuantity}</td>
                </tr>
              </tbody>
            </table>

            ${(order.procurement_date || order.production_date || order.delivery_date) ? `
            <div class="dates-section">
              <h4 style="margin-bottom: 15px;">Timeline</h4>
              <div class="dates-grid">
                <div class="date-item">
                  <div class="date-label">Procurement Date</div>
                  <div class="date-value">${order.procurement_date ? formatDate(order.procurement_date) : '-'}</div>
                </div>
                <div class="date-item">
                  <div class="date-label">Production Date</div>
                  <div class="date-value">${order.production_date ? formatDate(order.production_date) : '-'}</div>
                </div>
                <div class="date-item">
                  <div class="date-label">Delivery Date</div>
                  <div class="date-value">${order.delivery_date ? formatDate(order.delivery_date) : '-'}</div>
                </div>
              </div>
            </div>
            ` : ''}

            ${order.notes ? `
            <div class="notes-section">
              <h4>Notes:</h4>
              <p>${order.notes}</p>
            </div>
            ` : ''}

            <div class="signatures">
              <div class="sig-line">Production Manager</div>
              <div class="sig-line">Quality Control</div>
            </div>
            <div class="footer">
              <p>Printed on: ${formatDate(new Date())}</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return { handlePrint };
};
